import { Skeleton } from '@/modules/shared/components/skeleton/Skeleton';

/**
 * Login page loading skeleton.
 *
 * Mirrors the structural layout of ClassicTemplate (the default login
 * template) exactly — same wrapper classes, breakpoints and padding — so
 * there is no layout shift when the real content mounts.
 */
export default function LoginPageSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      role="status"
      className="min-h-[100dvh] w-full flex flex-col lg:flex-row lg:h-[100dvh] lg:overflow-hidden"
    >
      <span className="sr-only">Loading login page</span>

      {/* Left Panel – Branding (hidden on mobile, matches ClassicTemplate) */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative overflow-hidden bg-gradient-to-br from-primary-soft via-background to-secondary-soft">
        <div className="relative z-10 flex flex-col justify-between min-h-screen w-full px-6 lg:px-10 xl:px-14 py-10 lg:py-12 xl:py-16">
          <div className="flex-1">
            {/* Logo */}
            <div className="mb-6">
              <Skeleton className="h-[65px] xl:h-[73px] w-[180px]" />
            </div>
            {/* Title + description */}
            <div className="mb-8 xl:mb-10 space-y-3">
              <Skeleton className="h-8 xl:h-10 w-3/4" />
              <Skeleton className="h-4 w-full max-w-lg" />
              <Skeleton className="h-4 w-2/3 max-w-lg" />
            </div>
            {/* Feature cards */}
            <div className="grid grid-cols-2 gap-4 mb-8 xl:mb-10">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))}
            </div>
          </div>
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 pt-6 lg:pt-8 border-t border-card/20">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel – Login Form */}
      <div className="relative flex-1 flex flex-col lg:overflow-y-auto bg-background">
        {/* Mobile Header */}
        <div className="lg:hidden px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-primary-soft to-secondary-soft">
          <div className="flex items-center justify-center gap-2">
            <Skeleton className="h-[45px] w-[45px] sm:h-[49px] sm:w-[49px] rounded-lg" />
            <div className="space-y-1.5">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        </div>

        {/* Form Container */}
        <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-4 sm:px-6 sm:py-4 md:px-8 lg:px-10 xl:px-12">
          <div className="w-full max-w-md">
            {/* Logo (right panel, small screens where left panel is hidden) */}
            <div className="hidden sm:flex justify-center mb-4 lg:hidden">
              <Skeleton className="h-10 w-32" />
            </div>

            {/* Welcome text */}
            <div className="text-center mb-4 sm:mb-5 space-y-2">
              <Skeleton className="h-6 w-40 mx-auto" />
              <Skeleton className="h-4 w-56 mx-auto" />
            </div>

            {/* Login card */}
            <div className="p-4 sm:p-5 space-y-3.5 rounded-lg border border-border/50">
              {/* Username field */}
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
              {/* Password field */}
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
              {/* Forgot password */}
              <div className="flex justify-end">
                <Skeleton className="h-3 w-24" />
              </div>
              {/* Sign in button */}
              <Skeleton className="h-10 w-full rounded-md mt-1" />
            </div>

            {/* Help text */}
            <div className="mt-2 flex justify-center">
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-center py-3 px-4 flex-shrink-0 space-y-1.5">
          <Skeleton className="h-3 w-40 mx-auto" />
          <Skeleton className="h-3 w-52 mx-auto" />
        </div>
      </div>
    </div>
  );
}
